import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { getTheme, ThemeName } from '../themes/themes';
import './ThemeSwitcher.css';

const ThemeSwitcher: React.FC = () => {
  const { currentTheme, theme, setTheme, availableThemes } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleThemeChange = (themeName: ThemeName) => {
    setTheme(themeName);
    setIsOpen(false);
  };

  return (
    <div className="theme-switcher" ref={dropdownRef}>
      <button
        className="theme-switcher-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Change theme"
        aria-expanded={isOpen}
      >
        <span className="theme-switcher-icon">🎨</span>
        <span className="theme-switcher-label">{theme.displayName}</span>
        <span className={`theme-switcher-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>

      {isOpen && (
        <div className="theme-switcher-dropdown">
          <div className="theme-switcher-header">Choose Theme</div>
          {availableThemes.map((themeName) => {
            const themeOption = getTheme(themeName);
            const isActive = currentTheme === themeName;
            return (
              <button
                key={themeName}
                className={`theme-switcher-option ${isActive ? 'active' : ''}`}
                onClick={() => handleThemeChange(themeName)}
              >
                <div className="theme-option-content">
                  <div className="theme-option-preview" style={{ backgroundColor: themeOption.colors.background }}></div>
                  <div className="theme-option-info">
                    <div className="theme-option-name">{themeOption.displayName}</div>
                    <div className="theme-option-description">{themeOption.description}</div>
                  </div>
                  {isActive && <span className="theme-option-check">✓</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ThemeSwitcher;

